/**
 * Login Page Component
 * Handles user authentication and token storage
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Alert, CircularProgress, IconButton, InputAdornment
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, loading: authLoading, authReady } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authInfo, setAuthInfo] = useState<any>(null);
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });

  // Redirect if already authenticated (wait for authReady to prevent race conditions)
  useEffect(() => {
    if (authReady && isAuthenticated && !authLoading) {
      navigate('/dashboard');
    }
  }, [authReady, isAuthenticated, authLoading, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setAuthInfo(null);
    setLoading(true);

    try {
      const result = await login(formData.username, formData.password);

      // Capture auth method info (result is always present on success)
      setAuthInfo(result);

      // Login success - context will update and redirect via useEffect
      navigate('/dashboard');
    } catch (err: any) {
      console.error('Login error:', err);

      // Build error message with auth method info
      let errorMsg = 'Invalid username or password';

      if (err.response?.data) {
        const authMethod = err.response.data.auth_method || 'unknown';
        const adamStatus = err.response.data.adam_status || 'unknown';
        const fallbackReason = err.response.data.fallback_reason;

        if (err.response.status === 401 || err.response.status === 400) {
          errorMsg = `Authentication failed (method: ${authMethod}, ADAM: ${adamStatus})`;
          if (fallbackReason) {
            errorMsg += `\nReason: ${fallbackReason}`;
          }
        } else {
          errorMsg = err.response.data.detail || err.response.data.error || 'Login failed. Please try again.';
        }
      }

      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
      }}
    >
      <Paper sx={{ p: 4, maxWidth: 400, width: '100%' }}>
        <Typography variant="h4" gutterBottom textAlign="center">
          Caliper LMS
        </Typography>
        <Typography variant="body2" color="textSecondary" textAlign="center" mb={3}>
          Sign in to continue
        </Typography>

        {authInfo && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setAuthInfo(null)}>
            <strong>Authentication Successful!</strong>
            <br />
            Method: {authInfo.auth_method === 'adam' ? 'ADAM (External)' : 'Local Database'}
            <br />
            ADAM Status: {authInfo.adam_status}
            {authInfo.debug && (
              <>
                <br />
                <small style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>
                  Debug: ADAM={authInfo.debug.adam_attempted ? (authInfo.debug.adam_success ? '✓' : '✗') : '-'}
                  , Local={authInfo.debug.local_attempted ? (authInfo.debug.local_success ? '✓' : '✗') : '-'}
                  {authInfo.debug.fallback_reason && `, Reason: ${authInfo.debug.fallback_reason}`}
                </small>
              </>
            )}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Username"
            name="username"
            value={formData.username}
            onChange={handleChange}
            margin="normal"
            required
            disabled={loading}
            autoFocus
          />

          <TextField
            fullWidth
            label="Password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleChange}
            margin="normal"
            required
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                    disabled={loading}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
            sx={{ mt: 3 }}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>
      </Paper>
    </Box>
  );
};

export default LoginPage;
